import {
  Avatar,
  Box,
  Divider,
  Fade,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import LucideIcon from "../../../../components/LucideIcon";
import instagramLogo from "../../../../img/instagram.png";
import linkedinLogo from "../../../../img/linkedin.png";
import { SampleContentPiece, SampleContentResult } from "../types";
import StepNavButtons from "./StepNavButtons";

interface ContentSamplesStepProps {
  samples?: SampleContentResult;
  onNext: () => void;
  onBack: () => void;
}

interface Brand {
  companyName: string;
  logoUrl: string;
}

// Blog bodies run long; show only an excerpt in the preview so the article doesn't dominate the
// screen. Trim to a character budget at a paragraph break (or word boundary) and flag that more
// follows so we can show a "Continue reading" hint — the full text lives in the saved draft.
const BLOG_EXCERPT_CHARS = 700;
function blogExcerpt(body: string): { text: string; truncated: boolean } {
  const full = body || "";
  if (full.length <= BLOG_EXCERPT_CHARS) return { text: full, truncated: false };
  const slice = full.slice(0, BLOG_EXCERPT_CHARS);
  // Prefer cutting at the last paragraph break; fall back to the last word boundary.
  const lastBreak = slice.lastIndexOf("\n\n");
  const cut = lastBreak > BLOG_EXCERPT_CHARS * 0.5 ? lastBreak : slice.lastIndexOf(" ");
  return { text: full.slice(0, cut > 0 ? cut : BLOG_EXCERPT_CHARS).trimEnd(), truncated: true };
}

// A small brand avatar: the company logo when we have one, otherwise the first initial on a
// primary-tinted circle. Used as the author avatar in the social mockups and the blog byline.
const BrandAvatar: React.FC<{ brand: Brand; size?: number }> = ({ brand, size = 40 }) => {
  const initial = (brand.companyName || "?").trim().charAt(0).toUpperCase();
  return (
    <Avatar
      src={brand.logoUrl || undefined}
      sx={{ width: size, height: size, bgcolor: "primary.main", fontWeight: 700 }}
      variant="circular"
    >
      {initial}
    </Avatar>
  );
};

// Blog post rendered as an article card: wide hero image, title, markdown excerpt, brand byline.
const BlogMockup: React.FC<{ piece: SampleContentPiece; brand: Brand }> = ({ piece, brand }) => {
  const theme = useTheme();
  const { text: excerpt, truncated } = blogExcerpt(piece.body || "");
  return (
    <Box
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        backgroundColor: "background.paper",
      }}
    >
      {piece.imageUrl && (
        <Box
          component="img"
          src={piece.imageUrl}
          alt={piece.title || "Blog hero"}
          sx={{ width: "100%", aspectRatio: "16 / 9", objectFit: "cover", display: "block" }}
        />
      )}
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <LucideIcon name="FileText" size={16} color={theme.palette.text.secondary} />
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
            Blog Article
          </Typography>
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5, lineHeight: 1.25 }}>
          {piece.title}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <BrandAvatar brand={brand} size={32} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              {brand.companyName || "Your Company"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              5 min read
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            position: "relative",
            "& p": { mb: 1.5, lineHeight: 1.7, color: "text.primary" },
            "& h1,& h2,& h3": { fontWeight: 700, mt: 2, mb: 1 },
            "& ul,& ol": { pl: 3, mb: 1.5 },
            "& li": { mb: 0.5 },
            "& > *:last-child": { mb: 0 },
            color: "text.primary",
          }}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{excerpt}</ReactMarkdown>
        </Box>
        {truncated && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mt: 1.5,
              color: "primary.main",
              fontWeight: 600,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Continue reading
            </Typography>
            <LucideIcon name="ArrowRight" size={16} color={theme.palette.primary.main} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

// LinkedIn post mockup: author header (logo + company + connection degree + time), post text,
// the generated image, and a faux reaction/comment action bar.
const LinkedInMockup: React.FC<{ piece: SampleContentPiece; brand: Brand }> = ({ piece, brand }) => {
  const theme = useTheme();
  // LinkedIn shows the body as the post text; the title (if any) leads it in bold.
  const text = [piece.title, piece.body].filter(Boolean).join("\n\n");
  return (
    <Box
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        backgroundColor: "background.paper",
      }}
    >
      <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
        <BrandAvatar brand={brand} size={48} />
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {brand.companyName || "Your Company"}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap component="div">
            Promoting your business · 1st
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Now · 🌐
          </Typography>
        </Box>
        <Box component="img" src={linkedinLogo} alt="LinkedIn" sx={{ width: 24, height: 24 }} />
      </Box>

      <Typography variant="body2" sx={{ px: 2, pb: 1.5, whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
        {text}
      </Typography>

      {piece.imageUrl && (
        <Box
          component="img"
          src={piece.imageUrl}
          alt={piece.title || "LinkedIn post"}
          sx={{ width: "100%", aspectRatio: "1 / 1", objectFit: "cover", display: "block" }}
        />
      )}

      <Divider />
      <Box sx={{ display: "flex", justifyContent: "space-around", py: 1 }}>
        {[
          { icon: "ThumbsUp", label: "Like" },
          { icon: "MessageCircle", label: "Comment" },
          { icon: "Repeat2", label: "Repost" },
          { icon: "Send", label: "Send" },
        ].map((a) => (
          <Box key={a.label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <LucideIcon name={a.icon} size={18} color={theme.palette.text.secondary} />
            <Typography variant="caption" color="text.secondary">
              {a.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// Instagram post mockup: header (avatar + handle), square image, action row, caption with the
// company handle in bold leading the text.
const InstagramMockup: React.FC<{ piece: SampleContentPiece; brand: Brand }> = ({ piece, brand }) => {
  const theme = useTheme();
  const handle = (brand.companyName || "yourcompany").toLowerCase().replace(/[^a-z0-9]+/g, "");
  // IG captions live in the body; titles aren't shown separately on IG.
  const caption = piece.body || piece.metaDescription || "";
  return (
    <Box
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        backgroundColor: "background.paper",
      }}
    >
      <Box sx={{ p: 1.5, display: "flex", alignItems: "center", gap: 1.25 }}>
        <BrandAvatar brand={brand} size={32} />
        <Typography variant="body2" sx={{ fontWeight: 700, flexGrow: 1 }}>
          {handle}
        </Typography>
        <LucideIcon name="MoreHorizontal" size={20} color={theme.palette.text.secondary} />
      </Box>

      {piece.imageUrl ? (
        <Box
          component="img"
          src={piece.imageUrl}
          alt={piece.title || "Instagram post"}
          sx={{ width: "100%", aspectRatio: "1 / 1", objectFit: "cover", display: "block" }}
        />
      ) : (
        <Box
          sx={{
            width: "100%",
            aspectRatio: "1 / 1",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "action.hover",
          }}
        >
          <LucideIcon name="Image" size={40} color={theme.palette.text.secondary} />
        </Box>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, px: 1.5, pt: 1.25 }}>
        <LucideIcon name="Heart" size={24} color={theme.palette.text.primary} />
        <LucideIcon name="MessageCircle" size={24} color={theme.palette.text.primary} />
        <LucideIcon name="Send" size={24} color={theme.palette.text.primary} />
        <Box sx={{ flexGrow: 1 }} />
        <LucideIcon name="Bookmark" size={24} color={theme.palette.text.primary} />
      </Box>

      <Typography variant="body2" sx={{ px: 1.5, py: 1.25, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
        <Box component="span" sx={{ fontWeight: 700, mr: 0.75 }}>
          {handle}
        </Box>
        {caption}
      </Typography>
    </Box>
  );
};

function renderPiece(piece: SampleContentPiece, brand: Brand) {
  switch (piece.channel) {
    case "blog_post":
      return <BlogMockup piece={piece} brand={brand} />;
    case "linkedin_post":
      return <LinkedInMockup piece={piece} brand={brand} />;
    case "instagram_post":
      return <InstagramMockup piece={piece} brand={brand} />;
    default:
      return null;
  }
}

// Tab label + icon for a piece's channel. Social channels use their real brand logos (the same
// PNGs the Integrations page uses); the blog falls back to a Lucide glyph (no brand mark needed).
const brandTabIcon = (src: string) => (
  <Box component="img" src={src} alt="" sx={{ width: 18, height: 18 }} />
);

function channelTab(channel?: string): { label: string; icon: React.ReactElement } {
  switch (channel) {
    case "blog_post":
      return { label: "Blog", icon: <LucideIcon name="FileText" size={18} /> };
    case "linkedin_post":
      return { label: "LinkedIn", icon: brandTabIcon(linkedinLogo) };
    case "instagram_post":
      return { label: "Instagram", icon: brandTabIcon(instagramLogo) };
    default:
      return { label: "Post", icon: <LucideIcon name="FileText" size={18} /> };
  }
}

// Onboarding preview of 3 AI-generated sample content pieces, each rendered styled like the
// environment it would be published in (blog article, LinkedIn post, Instagram post), stacked.
export default function ContentSamplesStep({
  samples,
  onNext,
  onBack,
}: ContentSamplesStepProps) {
  const theme = useTheme();
  const pieces = samples?.pieces || [];
  const brand: Brand = {
    companyName: samples?.brand?.companyName || "",
    logoUrl: samples?.brand?.logoUrl || "",
  };

  const [tab, setTab] = useState(0);
  // Guard against the active tab pointing past the available pieces (e.g. fewer than 3 generated).
  const activeTab = Math.min(tab, Math.max(0, pieces.length - 1));
  const activePiece = pieces[activeTab];

  return (
    <Fade in timeout={600}>
      <Box>
        <Typography variant="overline" color="primary" sx={{ fontWeight: 700 }}>
          Content
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}>
          Sample content, ready to publish
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
          We wrote three pieces from your content plan — a blog post, a LinkedIn post, and an
          Instagram post — each with an on-brand image, shown how they'd look once published.
        </Typography>

        {!pieces.length ? (
          <Typography color="text.secondary">
            We couldn't generate sample content this time. You can create content anytime from the
            Content page.
          </Typography>
        ) : (
          <>
            <Tabs
              value={activeTab}
              onChange={(_e, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{ mb: 3, borderBottom: "1px solid", borderColor: "divider" }}
            >
              {pieces.map((piece, i) => {
                const t = channelTab(piece.channel);
                return (
                  <Tab
                    key={i}
                    icon={t.icon}
                    iconPosition="start"
                    label={t.label}
                    sx={{ textTransform: "none", minHeight: 48 }}
                  />
                );
              })}
            </Tabs>

            {/* Re-mount on tab change so the Fade replays and the new mockup animates in. */}
            <Fade in key={activeTab} timeout={300}>
              <Box>{activePiece && renderPiece(activePiece, brand)}</Box>
            </Fade>

            <Box
              sx={{
                mt: 3,
                p: 2,
                borderRadius: 2,
                backgroundColor: "action.hover",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <LucideIcon name="Save" size={20} color={theme.palette.primary.main} />
              <Typography variant="body2" color="text.secondary">
                These are saved as drafts in your Content library — edit and publish them anytime
                after setup.
              </Typography>
            </Box>
          </>
        )}

        <StepNavButtons onBack={onBack} onNext={onNext} />
      </Box>
    </Fade>
  );
}
